import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, MouseEvent } from 'react';

import { Opponent } from '@gem/call/CallStore/Opponent/Opponent';
import { AvatarTypes } from '@gem/call/Removable/components/Avatar/Avatar';
import { DotsIcon } from '@gem/icons';
import { IconButton, IconViewSizes, PopoverMenu, usePopover } from '@gem/ui-kit';

import { NoVideoPlaceholder } from './NoVideoPlaceholder/NoVideoPlaceholder';
import { OpponentMeta } from './OpponentMeta/OpponentMeta';
import { useOpponentVideoPresenter } from './OpponentVideo.presenter';
import styles from './OpponentVideo.styles.m.css';
import { OpponentVideoView } from './OpponentVideoView/OpponentVideoView';

interface IOpponentVideoProps {
  opponent: Opponent;
  avatarType: AvatarTypes;
  containerClassName?: string;
  className?: string;
  isMinimize?: boolean;
  isHighlightAudio?: boolean;
  opponentPosition?: number;
  isRightList?: boolean;
  onClick?(): void;
}

export const OpponentVideo: FC<IOpponentVideoProps> = observer(
  ({
    opponent,
    avatarType,
    containerClassName,
    className,
    isMinimize,
    onClick,
    isHighlightAudio,
    opponentPosition,
    isRightList,
  }) => {
    const popover = usePopover<HTMLButtonElement>();
    const presenter = useOpponentVideoPresenter({ opponent, popover, onClick, isHighlightAudio });

    const positionStyles = typeof opponentPosition === 'number' ? { order: opponentPosition } : {};

    const handleContainerMouseLeave = (): void => {
      if (popover.isOpened) {
        popover.close();
      }
    };

    const handleMenuClick = (event: MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      event.preventDefault();

      if (!presenter.actions.length) {
        return;
      }

      popover.toggle();
    };

    return (
      <>
        <div
          className={classNames(styles.container, containerClassName)}
          style={positionStyles}
          role="button"
          tabIndex={0}
          onClick={presenter.onClick}
          onMouseLeave={handleContainerMouseLeave}
        >
          <div
            className={classNames(styles.borderContainer, {
              [styles.isSpeaking]: presenter.isSpeaking && !isMinimize,
              [styles.withRiseHand]: presenter.isRiseHand,
            })}
          />
          {presenter.opponent.hasVideo ? (
            <OpponentVideoView
              playVideo={presenter.playVideo}
              stopVideo={presenter.stopVideo}
              uid={presenter.opponent.uid}
              isPlayingVideo={presenter.opponent.isPlayingVideo}
              hasVideo={presenter.opponent.hasVideo}
              className={className}
              inverted={presenter.isMeOpponent}
            />
          ) : (
            <NoVideoPlaceholder
              opponent={presenter.opponent}
              type={avatarType}
            />
          )}
          <OpponentMeta
            isMinimize={isMinimize}
            isRightList={isRightList}
            opponent={presenter.opponent}
          />
          {presenter.canShowActionsButton && (
            <>
              <IconButton
                setRef={popover.containerRef}
                Icon={DotsIcon}
                className={classNames(styles.menuButton, {
                  [styles.menuButtonVisible]: popover.isOpened,
                })}
                size={IconViewSizes.Big}
                theme="12"
                isActive={popover.isOpened}
                onClick={handleMenuClick}
              />

              <PopoverMenu
                className={styles.menu}
                isOpened={popover.isOpened}
                menuItems={presenter.actions}
                onClose={popover.close}
                reference={popover.containerRef.current}
                placement="bottom-end"
                theme={'dark'}
              />
            </>
          )}
        </div>
      </>
    );
  }
);
